import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ChildrenSelectorComponent,
  Student,
} from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { TokenService } from '../../services/id_token.service';
import { NotificationService } from '../../services/notification.service';
import { environment } from '../../../environments/environment';
@Component({
  standalone: true,
  selector: 'app-photos',
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    ChildrenSelectorComponent,
    PageTitleComponent,
  ],
  templateUrl: './Album.component.html',
  styleUrls: ['./Album.component.css'],
})
export class AlbumsComponent implements OnInit {
  private readonly apiBaseUrl = environment.apiUrl.replace(/\/$/, '');
  selectedStudent: Student | null = null;
  selectedPhoto: any = null;
  currentImageIndex = 0;
  private destroy$ = new Subject<void>();
  themeName: string = '';
  photos: {
    id: string;
    studentId: string;
    src: string;
    alt: string;
    title: string;
    creationDate: string;
  }[] = [];

  // Variables pour gérer les animations modernes
  isAnimating = false;
  animationDirection: 'left' | 'right' | 'none' = 'none';
  currentAnimation: 'slide' | 'wave' | 'zoom' | 'flip' | 'fade-rotate' =
    'slide';

  // Durées d'animation en millisecondes
  private readonly ANIMATION_DURATION = 600;
  private readonly WAVE_ANIMATION_DURATION = 800;

  readonly defaultImage =
    'https://e7.pngegg.com/pngimages/199/374/png-clipart-graph-album-graphy-icon-camera-with-s-of-landscapes-album-photography-thumbnail.png';

  constructor(
    private router: Router,
    private http: HttpClient,
    private tokenService: TokenService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit() {
    this.initializeCarousel();
  }

  private initializeCarousel() {
    // Initialisation avec animation d'entrée
    setTimeout(() => {
      this.animationDirection = 'none';
      this.currentAnimation = 'wave';
    }, 100);
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.loadStudentPhotos(student);
  }

  onStudentsLoaded(students: Student[]) {
    console.log('Students loaded:', students.length);
  }

  private loadStudentPhotos(student: Student) {
    // Ancienne API pour lister les thèmes (à remplacer plus tard si possible)
    const themesUrl =
      `${this.apiBaseUrl}/gallery/ListgalleryTheme/${student.idStudent}`.trim();

    this.showLoadingEffect();

    this.http.get<any>(themesUrl).subscribe({
      next: (resp) => {
        // certains retours peuvent être encadrés dans { response: [...] }
        let themes: any[] = [];
        if (Array.isArray(resp)) {
          themes = resp;
        } else if (resp && Array.isArray(resp.response)) {
          themes = resp.response;
        } else {
          console.warn('Unexpected themes response shape', resp);
        }

        // Pour chaque thème, appelle la NOUVELLE API

        const albumRequests = themes.map((theme) => {
          const newApiUrl =
            `${this.apiBaseUrl}/gallery/listGalleryStudentByStudentAndThemeGallery/${student.idStudent}/${theme.id}`.trim();

          return this.http.get<any>(newApiUrl).pipe(
            map((response) => {
              // La réponse a la forme : { error: "...", response: [...] }
              const photos = response?.response || [];
              console.log(photos + 'photos');
              // si le thème contient déjà un chemin de couverture, l'utiliser
              let firstPhotoPath = 'https://via.placeholder.com/400x300/f8f9fa/6c757d?text=📷+Album';
              if (theme.coverPath) {
                firstPhotoPath = `https://plateforme.educanet.pro/${theme.coverPath}`.replace(/ /g, '');
              } else if (photos.length > 0 && photos[0]?.galerie?.path) {
                firstPhotoPath = `https://plateforme.educanet.pro/${photos[0].galerie.path}`.replace(/ /g, '');
              }

              return {
                id: theme.id?.toString() || '',
                studentId: student.idStudent.toString(),
                src: firstPhotoPath,
                alt: theme.name || 'Album sans titre',
                title: theme.name || 'Sans titre',
                creationDate: theme.creationDate || '',
              };
            }),
          );
        });

        forkJoin(albumRequests).subscribe({
          next: (albumsWithPhotos) => {
            this.photos = albumsWithPhotos;
            this.currentImageIndex = 0;
            this.isAnimating = false;
            this.animationDirection = 'none';
            this.currentAnimation = 'wave';
            this.hideLoadingEffect();
          },
          error: (err) => {
            console.error('Erreur lors du chargement des albums', err);
            this.photos = [];
            this.hideLoadingEffect();
          },
        });
      },
      error: (err) => {
        console.error('Échec du chargement des thèmes', err);
        this.photos = [];
        this.hideLoadingEffect();
      },
    });
  }
  moveToCenter(direction: 'left' | 'right') {
    if (this.isAnimating) return;

    this.startAnimation(direction);

    if (direction === 'left' && this.currentImageIndex > 0) {
      this.currentImageIndex--;
    } else if (
      direction === 'right' &&
      this.currentImageIndex < this.photos.length - 1
    ) {
      this.currentImageIndex++;
    }
  }

  previousImageFromPagination() {
    if (this.isPreviousDisabled() || this.isAnimating) return;

    this.currentAnimation = this.getRandomAnimation();
    this.startAnimation('right');
    this.currentImageIndex--;
  }

  nextImageFromPagination() {
    if (this.isNextDisabled() || this.isAnimating) return;

    this.currentAnimation = this.getRandomAnimation();
    this.startAnimation('left');
    this.currentImageIndex++;
  }

  goToImageFromPagination(index: number) {
    if (
      this.isAnimating ||
      index === this.currentImageIndex ||
      index < 0 ||
      index >= this.photos.length
    )
      return;

    const direction = index > this.currentImageIndex ? 'left' : 'right';
    this.currentAnimation = this.getRandomAnimation();
    this.startAnimation(direction);
    this.currentImageIndex = index;
  }

  onMouseWheel(event: WheelEvent) {
    event.preventDefault();

    if (this.isAnimating) return;

    const delta = event.deltaY || event.deltaX;
    this.currentAnimation = 'slide';

    if (delta > 0) {
      this.nextImageFromPagination();
    } else {
      this.previousImageFromPagination();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.isAnimating) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previousImageFromPagination();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextImageFromPagination();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.goToAlbum(this.getCenterImage());
        break;
    }
  }

  private startAnimation(direction: 'left' | 'right') {
    this.isAnimating = true;
    this.animationDirection = direction;

    const duration = this.getAnimationDuration();

    setTimeout(() => {
      this.isAnimating = false;
      this.animationDirection = 'none';
    }, duration);
  }

  private getAnimationDuration(): number {
    switch (this.currentAnimation) {
      case 'wave':
        return this.WAVE_ANIMATION_DURATION;
      case 'zoom':
        return 500;
      case 'flip':
      case 'fade-rotate':
        return 700;
      default:
        return this.ANIMATION_DURATION;
    }
  }

  private getRandomAnimation(): 'slide' | 'zoom' | 'flip' | 'fade-rotate' {
    const animations = ['slide', 'zoom', 'flip', 'fade-rotate'] as const;
    const randomIndex = Math.floor(Math.random() * animations.length);
    return animations[randomIndex];
  }

  getAnimationClass(): string {
    if (!this.isAnimating) {
      return this.currentAnimation === 'wave' ? 'animate-wave-in' : '';
    }

    const baseClass = 'animate-';

    switch (this.currentAnimation) {
      case 'slide':
        return `${baseClass}slide-from-${this.animationDirection}`;
      case 'zoom':
        return `${baseClass}zoom-in`;
      case 'flip':
        return `${baseClass}modern-flip`;
      case 'fade-rotate':
        return `${baseClass}fade-rotate`;
      default:
        return `${baseClass}slide-from-${this.animationDirection}`;
    }
  }

  private showLoadingEffect() {
    const centerImage = document.querySelector('.carousel-center-image');
    if (centerImage) {
      centerImage.classList.add('image-loading');
    }
  }

  private hideLoadingEffect() {
    const centerImage = document.querySelector('.carousel-center-image');
    if (centerImage) {
      centerImage.classList.remove('image-loading');
    }
  }

  isPreviousDisabled(): boolean {
    return this.currentImageIndex <= 0;
  }

  isNextDisabled(): boolean {
    return this.currentImageIndex >= this.photos.length - 1;
  }

  getCenterImage() {
    return this.photos[this.currentImageIndex] || null;
  }

  getLeftImage() {
    const leftIndex = this.currentImageIndex - 1;
    return leftIndex >= 0 ? this.photos[leftIndex] : null;
  }

  getRightImage() {
    const rightIndex = this.currentImageIndex + 1;
    return rightIndex < this.photos.length ? this.photos[rightIndex] : null;
  }

  goToAlbum(album: any) {
    if (!album?.studentId || !album?.id) {
      console.warn('Album data incomplete', album);
      return;
    }
    this.updateNotification(album.id, album.studentId);

    const token = this.tokenService.generateToken(
      album.studentId,
      album.id,
      album.title,
    );

    this.router.navigate(['/album-details', token]);
  }

  openPhotoModal(photo: any) {
    this.selectedPhoto = photo;
  }

  closePhotoModal() {
    this.selectedPhoto = null;
  }

  private updateNotification(albumId: number, studentId: number): void {
    this.notificationService
      .updateNotificationStatus({
        idType: albumId,
        idStudent: studentId,
        nameType: 'Galerie',
      })
      .subscribe({
        next: () => {
          this.notificationService
            .getNotificationCounts(String(this.selectedStudent?.idUserStudent))
            .subscribe();
        },
        error: (err) =>
          console.error('Erreur updateNotification (Galerie):', err),
      });
  }
}
